// QUANDO A NYTA VOLTA, depois de a pessoa gastar as mensagens do dia.
//
// ⚠️ O DIA DO LIMITE É O DIA EM UTC, e não o do Brasil. A edge `nyta-chat` conta o uso por
// `usage_date` em UTC e zera à meia-noite UTC — 21h em Brasília. A tela dizia "Volta amanhã", e
// para quem está no Brasil isso é falso: volta HOJE, às 21h. A regra abaixo é cópia da da edge
// (`tomorrowUTC` em supabase/functions/nyta-chat/index.ts); se uma mudar, a outra muda junto.
//
// ⚠️ E O `resetAt` NEM SEMPRE CHEGA. Só a recusa do servidor (429) o manda. No caso comum — a
// centésima mensagem passa e o contador chega pelo cabeçalho da resposta — ele vem nulo. O
// `useNytaChat` carimba a hora NO INSTANTE em que o contador chega (`proximaMeiaNoiteUTC()`), e
// isso importa: calculada depois, "a partir de agora", a liberação andaria junto com o relógio e
// nunca chegaria. O `liberacaoDaNyta` sem `resetAt` é só a rede de segurança.

/** A próxima meia-noite UTC depois de `agora` — o instante em que o servidor zera o contador. */
export const proximaMeiaNoiteUTC = (agora: Date = new Date()): Date =>
  new Date(Date.UTC(agora.getUTCFullYear(), agora.getUTCMonth(), agora.getUTCDate() + 1));

/** Quando o limite libera: o que o servidor disse, ou a regra dele. */
export const liberacaoDaNyta = (resetAt: string | null | undefined, agora: Date = new Date()): Date => {
  const dita = resetAt ? new Date(resetAt) : null;
  return dita && !Number.isNaN(dita.getTime()) ? dita : proximaMeiaNoiteUTC(agora);
};

/**
 * Ainda está no limite? Passada a hora da liberação, NÃO — mesmo com o contador em 100/100.
 * O contador na tela só se atualiza na próxima mensagem; sem esta conta, o cartão continuava
 * travando o campo depois de o servidor já ter zerado, até a pessoa recarregar a página.
 */
export const aindaNoLimite = (
  info: { count: number; limit: number; resetAt: string | null } | null | undefined,
  agora: Date = new Date(),
): boolean => !!info && info.count >= info.limit && liberacaoDaNyta(info.resetAt, agora).getTime() > agora.getTime();

const doisDigitos = (n: number) => String(n).padStart(2, '0');

/** "hoje às 21h00" / "amanhã às 21h00", no fuso de quem está vendo. */
export const quandoVolta = (quando: Date, agora: Date = new Date()): string => {
  const hora = `${doisDigitos(quando.getHours())}h${doisDigitos(quando.getMinutes())}`;
  const mesmoDia = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (mesmoDia(quando, agora)) return `hoje às ${hora}`;
  const amanha = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + 1);
  if (mesmoDia(quando, amanha)) return `amanhã às ${hora}`;
  return `${doisDigitos(quando.getDate())}/${doisDigitos(quando.getMonth() + 1)} às ${hora}`;
};

/** O cronômetro: "02:14:09". Nunca negativo. */
export const cronometro = (quando: Date, agora: Date = new Date()): string => {
  const total = Math.max(0, Math.ceil((quando.getTime() - agora.getTime()) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${doisDigitos(h)}:${doisDigitos(m)}:${doisDigitos(s)}`;
};
